import React, { useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';

// @mui material components
import Card from '@mui/material/Card';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import LocationPicker from 'components/LocationPicker';

// Authentication layout components
import CoverLayout from 'layouts/authentication/components/CoverLayout';

// Images
import bgImage from 'assets/images/bg-sign-up-cover.jpeg';
import jcgnLogo from 'assets/images/jcgn-logo.png';
import { sendOTP, verifyOTP, registerUser } from 'api/axios';

const steps = [
  'Email Verification',
  'Account Details',
  'Address Information',
  'Delivery Information',
];

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Cover() {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [OTP, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    streetNumber: '',
    email: '',
    profileName: '',
    streetName: '',
    APT: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    password: '',
    confirmPassword: '',
    // API requires imageUrl; delivery signup has no upload step
    imageUrl: 'https://jcgn.org/profilePicture.png',
    specificFields: {
      delivername: '',
      deliverphone: '',
      delivervehicle: '',
      delivervehiclecolor: '',
      delivervehiclemake: '',
      delivervehiclemodel: '',
      delivervehiclenumber: '',
      taxidriverid: '',
      deliverlocation: '',
      deliverstatus: true,
    },
    roleId: '7',
  });

  const handleOTPSubmit = async (e) => {
    e.preventDefault();

    if (!otpSent) {
      if (!email) {
        setError('Please enter an email address');
        return;
      }

      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const response = await sendOTP(email);
        setSuccess(response.message || 'OTP sent successfully!');
        setOtpSent(true);
      } catch (err) {
        setError(err.message || 'Failed to send OTP. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      if (!OTP) {
        setError('Please enter the OTP');
        return;
      }

      if (OTP.length !== 6) {
        setError('OTP must be 6 digits');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await verifyOTP({ email, OTP });
        setSuccess(response.message || 'OTP verified successfully!');
        setOtpVerified(true);
        setFormData((prev) => ({ ...prev, email }));
        handleNext();
      } catch (err) {
        setError(
          err.response?.data?.message || 'Invalid OTP. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validateStep(3)) {
      setError('Please enter all required delivery fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        email: formData.email || email,
        imageUrl: formData.imageUrl || 'https://jcgn.org/profilePicture.png',
        specificFields: {
          ...formData.specificFields,
          delivername:
            formData.specificFields.delivername?.trim() ||
            formData.profileName?.trim() ||
            'Delivery Driver',
          deliverlocation:
            formData.specificFields.deliverlocation?.trim() ||
            [formData.streetNumber, formData.streetName, formData.city, formData.country]
              .filter(Boolean)
              .join(', '),
        },
      };
      await registerUser(payload);
      setSuccess('Registration successful! Redirecting to sign in...');
      setSnackbarMessage('Registration successful! You can now sign in to your account.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/authentication/sign-in');
      }, 2500);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Registration failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSpecificFieldChange = (e) => {
    const { name, value } = e.target;
    const processedValue = name === 'deliverstatus' ? value === 'true' : value;
    setFormData((prev) => ({
      ...prev,
      specificFields: { ...prev.specificFields, [name]: processedValue },
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
    setSuccess(null);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
    setSuccess(null);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.password.trim() !== '' &&
          formData.confirmPassword.trim() !== '' &&
          formData.profileName.trim() !== '' &&
          // formData.imageUrl.trim() !== '' &&
          formData.password === formData.confirmPassword
        );
      case 2:
        return (
          formData.streetNumber.trim() !== '' &&
          formData.streetName.trim() !== '' &&
          formData.APT.trim() !== '' &&
          formData.city.trim() !== '' &&
          formData.state.trim() !== '' &&
          formData.country.trim() !== '' &&
          formData.zipCode.trim() !== ''
        );
      case 3:
        return (
          formData.specificFields.deliverphone.trim() !== '' &&
          formData.specificFields.delivervehicle.trim() !== '' &&
          formData.specificFields.delivervehiclemake.trim() !== '' &&
          formData.specificFields.delivervehiclemodel.trim() !== '' &&
          formData.specificFields.delivervehiclenumber.trim() !== ''
        );
      default:
        return true;
    }
  };

  const handleButtonClick1 = () => {
    if (isLoading || !validateStep(1)) {
      setSnackbarMessage('Please complete all required fields before proceeding.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      handleNext();
    }
  };

  const handleButtonClick2 = () => {
    if (isLoading || !validateStep(2)) {
      setSnackbarMessage('Please complete all required fields before proceeding.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } else {
      handleNext();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <MDBox component="form" role="form" onSubmit={handleOTPSubmit}>
            <MDBox mb={2}>
              {otpSent ? (
                <MDInput
                  type="text"
                  label="OTP"
                  variant="standard"
                  fullWidth
                  value={OTP}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  inputProps={{ maxLength: 6, pattern: '\\d*' }}
                />
              ) : (
                <MDInput
                  type="email"
                  label="Email"
                  variant="standard"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </MDBox>

            {error && (
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            )}
            {success && (
              <MDTypography
                variant="caption"
                color="success"
                fontWeight="medium"
              >
                {success}
              </MDTypography>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? 'Processing...'
                  : otpSent
                  ? 'Verify OTP'
                  : 'Send OTP'}
              </MDButton>
            </MDBox>

            {otpSent && (
              <MDBox textAlign="center" mb={2}>
                <MDTypography
                  variant="button"
                  color="info"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp('');
                    setError(null);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Change Email
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        );
      case 1:
        return (
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                value={email}
                disabled
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Password"
                variant="standard"
                fullWidth
                value={formData.password}
                onChange={handleInputChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                label="Confirm Password"
                variant="standard"
                fullWidth
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={togglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="text"
                name="profileName"
                label="Deliverer Name"
                variant="standard"
                fullWidth
                value={formData.profileName}
                onChange={handleInputChange}
              />
            </MDBox>

            {/* <MDBox mb={2}>
              <MDInput
                type="text"
                name="imageUrl"
                label="Image URL"
                variant="standard"
                fullWidth
                value={formData.imageUrl}
                onChange={handleInputChange}
              />
            </MDBox> */}

            {error && (
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            )}

            <MDBox display="flex" justifyContent="space-between" mt={4}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </MDButton>
              {/* <MDButton
                variant="gradient"
                color="info"
                onClick={handleNext}
                disabled={
                  isLoading || !formData.password || !formData.confirmPassword
                }
              >
                Next
              </MDButton> */}
              <MDButton
                variant="gradient"
                color="info"
                // onClick={handleNext}
                onClick={handleButtonClick1}
                // disabled={isLoading || !validateStep(1)}
              >
                Next
              </MDButton>
            </MDBox>
          </MDBox>
        );
      case 2:
        return (
          <MDBox component="form" role="form">
            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="streetNumber"
                label="Street Number"
                variant="standard"
                fullWidth
                value={formData.streetNumber}
                onChange={handleInputChange}
              />
              <MDInput
                type="text"
                name="streetName"
                label="Street Name"
                variant="standard"
                fullWidth
                value={formData.streetName}
                onChange={handleInputChange}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="text"
                name="APT"
                label="Apartment/Unit"
                variant="standard"
                fullWidth
                value={formData.APT}
                onChange={handleInputChange}
              />
            </MDBox>

            <MDBox mb={2}>
              <LocationPicker
                value={formData.specificFields.deliverlocation}
                onChange={(coord) =>
                  setFormData((prev) => ({
                    ...prev,
                    specificFields: {
                      ...prev.specificFields,
                      deliverlocation: coord,
                    },
                  }))
                }
              />
            </MDBox>


            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="city"
                label="City"
                variant="standard"
                fullWidth
                value={formData.city}
                onChange={handleInputChange}
              />
              <MDInput
                type="text"
                name="state"
                label="State"
                variant="standard"
                fullWidth
                value={formData.state}
                onChange={handleInputChange}
              />
            </MDBox>

            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="country"
                label="Country"
                variant="standard"
                fullWidth
                value={formData.country}
                onChange={handleInputChange}
              />
              <MDInput
                type="number"
                name="zipCode"
                label="Zip Code"
                variant="standard"
                fullWidth
                value={formData.zipCode}
                onChange={handleInputChange}
              />
            </MDBox>

            {error && (
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            )}

            <MDBox display="flex" justifyContent="space-between" mt={4}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </MDButton>
              {/* <MDButton
                variant="gradient"
                color="info"
                onClick={handleNext}
                disabled={isLoading}
              >
                Next
              </MDButton> */}
              <MDButton
                variant="gradient"
                color="info"
                // onClick={handleNext}
                onClick={handleButtonClick2}
                // disabled={isLoading || !validateStep(2)}
              >
                Next
              </MDButton>
            </MDBox>
          </MDBox>
        );
      case 3:
        return (
          <MDBox component="form" role="form" onSubmit={handleFormSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="tel"
                name="deliverphone"
                label="Phone Number (with country code, e.g. +1 ...)"
                variant="standard"
                fullWidth
                value={formData.specificFields.deliverphone}
                onChange={handleSpecificFieldChange}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                Vehicle Type
              </MDTypography>
              <Select
                labelId="vehicle-type-label"
                id="vehicle-type"
                name="delivervehicle"
                value={formData.specificFields.delivervehicle}
                onChange={handleSpecificFieldChange}
                fullWidth
                variant="standard"
              >
                <MenuItem value="Bicycle / Scooter">Bicycle / Scooter</MenuItem>
                <MenuItem value="Motorcycle">Motorcycle</MenuItem>
                <MenuItem value="Sedan Car / SUV">Sedan Car / SUV</MenuItem>
                <MenuItem value="Three-Wheeler M.C.">Three-Wheeler M.C.</MenuItem>
                <MenuItem value="Small Pickup Truck">Small Pickup Truck</MenuItem>
                <MenuItem value="Light Pickup Truck / Cargo Van">Light Pickup Truck / Cargo Van</MenuItem>
              </Select>
            </MDBox>

            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="delivervehiclemake"
                label="Vehicle Make (e.g. Toyota)"
                variant="standard"
                fullWidth
                value={formData.specificFields.delivervehiclemake}
                onChange={handleSpecificFieldChange}
              />
              <MDInput
                type="text"
                name="delivervehiclemodel"
                label="Vehicle Model (e.g. Camry LE)"
                variant="standard"
                fullWidth
                value={formData.specificFields.delivervehiclemodel}
                onChange={handleSpecificFieldChange}
              />
            </MDBox>

            <MDBox display="flex" gap={2} mb={2}>
              <MDInput
                type="text"
                name="delivervehiclecolor"
                label="Vehicle Color"
                variant="standard"
                fullWidth
                value={formData.specificFields.delivervehiclecolor}
                onChange={handleSpecificFieldChange}
              />
              <MDInput
                type="text"
                name="delivervehiclenumber"
                label="Vehicle Plate Number"
                variant="standard"
                fullWidth
                value={formData.specificFields.delivervehiclenumber}
                onChange={handleSpecificFieldChange}
              />
            </MDBox>

            <MDBox mb={2}>
              <MDInput
                type="text"
                name="taxidriverid"
                label="Taxi Driver Membership ID Number (if applicable)"
                variant="standard"
                fullWidth
                value={formData.specificFields.taxidriverid}
                onChange={handleSpecificFieldChange}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="text"
                name="delivername"
                label="Full Name (must match official national ID)"
                variant="standard"
                fullWidth
                value={formData.specificFields.delivername}
                onChange={handleSpecificFieldChange}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                Deliver Status
              </MDTypography>
              <Select
                name="deliverstatus"
                value={formData.specificFields.deliverstatus}
                onChange={handleSpecificFieldChange}
                fullWidth
                variant="standard"
              >
                <MenuItem value={true}>Available</MenuItem>
                <MenuItem value={false}>Not Available</MenuItem>
              </Select>
            </MDBox>

            {error && (
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </MDTypography>
            )}
            {success && (
              <MDTypography
                variant="caption"
                color="success"
                fontWeight="medium"
              >
                {success}
              </MDTypography>
            )}

            <MDBox display="flex" justifyContent="space-between" mt={4}>
              <MDButton
                variant="outlined"
                color="info"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                type="submit"
                // disabled={isLoading}
                disabled={isLoading || !validateStep(3)}
              >
                {isLoading ? 'Processing...' : 'Complete Registration'}
              </MDButton>
            </MDBox>
          </MDBox>
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <CoverLayout image={bgImage}>
      <Card
        sx={{
          width: '100%',
          maxWidth: { xs: 'calc(100vw - 40px)', sm: '100%' },
          mx: 'auto',
          overflow: 'hidden',
        }}
      >
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={{ xs: 1, sm: 2 }}
          mt={-3}
          p={{ xs: 2.5, sm: 3 }}
          mb={1}
          textAlign="center"
        >
          <MDBox
            component="img"
            src={jcgnLogo}
            alt="JCGN Logo"
            sx={{
              width: { xs: 150, sm: 180 },
              height: { xs: 150, sm: 180 },
              objectFit: 'contain',
              borderRadius: '12px',
              mb: 1.5,
            }}
          />
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {activeStep === 0 ? 'Sign Up' : 'Complete Your Registration'}
          </MDTypography>
          {activeStep === 0 && (
            <MDTypography display="block" variant="button" color="white" my={1}>
              {otpSent
                ? 'Enter the 6-digit OTP sent to your email'
                : 'Enter your email to send OTP'}
            </MDTypography>
          )}
        </MDBox>

        <MDBox px={{ xs: 1, sm: 3 }} pt={3}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              px: { xs: 0.5, sm: 0 },
              '& .MuiStep-root': {
                minWidth: 0,
                px: { xs: 0, sm: 1 },
              },
              '& .MuiStepLabel-label': {
                maxWidth: { xs: 74, sm: 'none' },
                mx: 'auto',
                fontSize: { xs: '0.62rem', sm: '0.75rem' },
                lineHeight: 1.25,
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
              },
              '& .MuiStepConnector-alternativeLabel': {
                left: { xs: 'calc(-50% + 10px)', sm: '-50%' },
                right: { xs: 'calc(50% + 10px)', sm: '50%' },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </MDBox>

        <MDBox pt={2} pb={3} px={3}>
          {renderStepContent(activeStep)}
        </MDBox>

        {activeStep === 0 && (
          <MDBox mt={3} mb={3} textAlign="center">
            <MDTypography variant="button" color="text">
              Already have an account?{' '}
              <MDTypography
                component={Link}
                to="/authentication/sign-in"
                variant="button"
                color="info"
                fontWeight="medium"
                textGradient
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        )}
      </Card>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </CoverLayout>
  );
}

export default Cover;
